
import { useState, useMemo, Suspense, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";

const LoadingState = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <Card key={i} className="p-6">
        <Skeleton className="h-6 w-3/4 mb-4" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-2/3" />
      </Card>
    ))}
  </div>
);

const Demo = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, ready } = useTranslation('demo');
  const [selectedNiche, setSelectedNiche] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isPending, startTransition] = useTransition();

  const niches = [
    "restaurants",
    "retail",
    "beauty",
    "fitness",
    "hotels",
    "entertainment",
    "education",
    "healthcare"
  ];

  const filteredNiches = useMemo(() => {
    if (!searchQuery) return niches;
    
    return niches.filter(niche => {
      const keywords = t(`niches.${niche}.keywords`, '').toLowerCase();
      const title = t(`niches.${niche}.title`, '').toLowerCase();
      const description = t(`niches.${niche}.description`, '').toLowerCase();
      const query = searchQuery.toLowerCase();
      
      return keywords.includes(query) || 
             title.includes(query) || 
             description.includes(query);
    });
  }, [searchQuery, niches, t]);

  const handleContinue = () => {
    if (!selectedNiche) {
      toast({
        title: "Please select a business type",
        description: "Select your business type to continue with the customization.",
        variant: "destructive",
      });
      return;
    }

    navigate(`/brand-setup/${selectedNiche}`);
  };

  const handleSearch = (value: string) => {
    startTransition(() => {
      setSearchQuery(value);
    });
  };

  const handleNicheSelect = (niche: string) => {
    startTransition(() => {
      setSelectedNiche(niche);
      toast({
        title: t(`niches.${niche}.title`),
        description: t(`niches.${niche}.description`),
      });
    });
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Skeleton className="h-12 w-3/4 mx-auto mb-4" />
            <Skeleton className="h-6 w-2/3 mx-auto mb-8" />
            <div className="max-w-md mx-auto mb-8">
              <Skeleton className="h-10 w-full" />
            </div>
            <LoadingState />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('title')}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {t('subtitle')}
          </p>

          <div className="max-w-md mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder={t('selectPlaceholder')}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {isPending ? (
              <LoadingState />
            ) : (
              <>
                {filteredNiches.map((niche) => (
                  <Card
                    key={niche}
                    className={`p-6 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                      selectedNiche === niche 
                        ? 'ring-2 ring-primary bg-primary/5' 
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handleNicheSelect(niche)}
                  >
                    <h3 className="text-lg font-semibold mb-2">
                      {t(`niches.${niche}.title`)}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {t(`niches.${niche}.description`)}
                    </p>
                  </Card>
                ))}
              </>
            )}
          </div>

          {filteredNiches.length === 0 && !isPending && (
            <p className="text-gray-500 italic">
              No business types found matching your search.
            </p>
          )}

          <Button
            onClick={handleContinue}
            className="w-full max-w-md mx-auto"
            disabled={!selectedNiche || isPending}
            size="lg"
          >
            {t('continue')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Demo;
